/**
 * Iter-117 PART 7 / Iter-118.7 → 118.11 — OnSetKit embedded view.
 *
 * Loads the live OnSetKit landing page in an iframe so users feel
 * they never leave OnSetFlow. Falls back to "Open OnSetKit" if the
 * embed fails (X-Frame-Options / CSP block, network error, etc).
 *
 * Iter-118.14 — removed the "window-within-window" framing.
 *  • Previous (118.11) wrapped the iframe in `max-w-2xl mx-auto px-5
 *    pt-3 pb-3 rounded-md overflow-hidden` which made Kit feel like a
 *    boxed website preview on tablet/desktop.
 *  • Now: the iframe goes EDGE-TO-EDGE below the header. No padding,
 *    no rounded card, no inset box. Kit fills the full available app
 *    content area on every viewport.
 *  • The HEADER still uses `max-w-2xl mx-auto px-3` so the
 *    "Back to OnSetFlow" button stays locked to the same X position
 *    as every other top-level screen — no horizontal jump when
 *    navigating Overview → Kit.
 *
 * Lazy by route — `KitPage.jsx` is only mounted when the user
 * navigates to `/projects/:projectId/kit`.
 *
 * The iframe is rendered immediately on mount (no skeleton blocking
 * overlay). A tiny loading pill floats top-right and clears on
 * `onload` or after a 4 s safety timeout.
 *
 * Closing via Back defers to react-router (`navigate(-1)`) so the
 * previous screen's scroll/state is preserved.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { useRouteBase } from "../context/ApiBaseContext";


const KIT_URL = "https://onsetkit.carrd.co";
const LOAD_TIMEOUT_MS = 4000;


export default function KitPage() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const routeBase = useRouteBase();
    const iframeRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [embedFailed, setEmbedFailed] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setLoading(false), LOAD_TIMEOUT_MS);
        return () => clearTimeout(t);
    }, []);

    const onIframeLoad = () => setLoading(false);
    const onIframeError = () => {
        setLoading(false);
        setEmbedFailed(true);
    };
    const closeBack = () => {
        if (window.history.length > 1) navigate(-1);
        else navigate(`${routeBase}/${projectId}`);
    };

    return (
        <div
            data-testid="kit-page"
            className="fixed inset-0 z-40 flex flex-col"
            style={{ backgroundColor: "rgb(237, 237, 237)" }}
        >
            {/* Iter-118.11 — header band runs full width with the
                shared card backdrop, but the BUTTON sits inside the
                same `max-w-2xl mx-auto px-3` column ProjectHeader
                uses, so the back-button anchor is identical to
                every other top-level screen.
                Iter-118.16 — header background matches Carrd's body
                grey too so the page reads as one continuous surface
                from the back button down to Carrd's content card.
                No visible seam = no "preview frame" perception. */}
            <header
                className="shrink-0 border-b border-black/5"
                style={{ backgroundColor: "rgb(237, 237, 237)" }}
            >
                <div className="max-w-2xl mx-auto w-full px-3 h-14 flex items-center">
                    <button
                        type="button"
                        onClick={closeBack}
                        data-testid="kit-back"
                        className="tap inline-flex items-center gap-2 h-9 px-2 rounded-md text-sm text-neutral-700 hover:text-black hover:bg-black/5"
                    >
                        <ArrowLeft size={14} />
                        Back to OnSetFlow
                    </button>
                </div>
            </header>

            {/* Iter-118.16 — REAL fix for the "window-within-a-window"
                feel.
                  Diagnosis: Carrd's own body background is
                  `rgb(237, 237, 237)` (light grey) at EVERY viewport
                  width. Those grey "rails" the user keeps reporting
                  belong to Carrd's design, not OnSetFlow. The previous
                  fixes that constrained the iframe to max-w-2xl with
                  a white wrapper actually CREATED the visible seam
                  (white column over dark app bg, with Carrd's grey
                  rails inside) — exactly the "preview card" perception.
                  Fix: full-bleed iframe + wrapper background that
                  matches Carrd's body grey ON THE NOSE so there is no
                  visible boundary anywhere between OnSetFlow chrome
                  and Carrd content. The eye reads one continuous
                  surface with the OnSetKit white content card sitting
                  on it — same way it would look opening Carrd in a
                  fresh tab. No transform / zoom / scale on the iframe
                  (defensive inline `style` kills any inherited rules). */}
            <div
                className="flex-1 relative w-full"
                style={{ backgroundColor: "rgb(237, 237, 237)" }}
            >
                {embedFailed ? (
                    <div
                        data-testid="kit-fallback"
                        className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 gap-4 bg-card"
                    >
                        <div className="h-12 w-12 rounded-xl bg-amber-500/15 text-amber-300 flex items-center justify-center">
                            <AlertTriangle size={20} />
                        </div>
                        <div>
                            <h2 className="font-display text-xl font-bold text-white">
                                Unable to load Kit here
                            </h2>
                            <p className="text-sm text-neutral-400 mt-1 max-w-md">
                                OnSetKit blocks embedding from this domain.
                                Open it in a new tab to keep working — your
                                OnSetFlow project is still right here when
                                you come back.
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={closeBack}
                                data-testid="kit-fallback-back"
                            >
                                Back
                            </Button>
                            <Button asChild data-testid="kit-fallback-open">
                                <a href={KIT_URL} target="_blank" rel="noreferrer">
                                    <ExternalLink size={13} className="mr-1.5" />
                                    Open OnSetKit
                                </a>
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <iframe
                            ref={iframeRef}
                            title="OnSetKit"
                            src={KIT_URL}
                            data-testid="kit-iframe"
                            onLoad={onIframeLoad}
                            onError={onIframeError}
                            sandbox="allow-scripts allow-forms allow-popups allow-same-origin allow-popups-to-escape-sandbox"
                            style={{
                                // Defensive — kill any inherited transform/zoom
                                // so Carrd cannot be scaled by parent rules.
                                transform: "none",
                                zoom: 1,
                                backgroundColor: "rgb(237, 237, 237)",
                            }}
                            className="absolute inset-0 block w-full h-full border-0 m-0 p-0"
                        />
                        {loading ? (
                            <div
                                data-testid="kit-loading"
                                aria-hidden="true"
                                className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full bg-black/60 text-white text-[11px] backdrop-blur pointer-events-none"
                            >
                                <Loader2 size={12} className="animate-spin" />
                                Loading
                            </div>
                        ) : null}
                    </>
                )}
            </div>

            <p className="shrink-0 hidden">
                <Link to={`${routeBase}/${projectId}`}>·</Link>
            </p>
        </div>
    );
}
